import { CommonModule } from '@angular/common';
import { BrisbaneDatePipe } from '../../../shared/pipes/brisbane-date.pipe';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { AccordionComponent } from '../../../shared/components/dialog/student-faq/student-faq.component';
import { SubmitEventComponent } from '../../../shared/components/dialog/submit-event/submit-event.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';
import { StudentPostLoginFlowService } from '../../../shared/services/student-post-login-flow.service';
import { UrlParserService } from '../../../shared/services/url-parser.service';

interface APIResponse {
  status: boolean;
  message: string;
  data: any;
}

@Component({
  selector: 'app-dashboard-home',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TableComponent, BrisbaneDatePipe],
  templateUrl: './dashboard-home.component.html',
  styleUrls: ['./dashboard-home.component.scss'],
})
export class UserComponent implements OnInit {
  @ViewChild('actionTpl') actionTpl!: TemplateRef<any>;
  isEditPage = false;
  teacherProfileImage = null;

  columns: string[] = [
    'Event name',
    'source',
    'Date & time',
    'location',
    'status',
  ];
  secondarColumns: string[] = [
    'Event name',
    'Teacher name',
    'Date & time',
    'location',
    'status',
  ];
  rows: any[] = [];
  nextEvents: any = [];
  loading = false;
  selectedSegment: 'admin' | 'teacher' | 'student' = 'admin';

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';
  currentPage = 1;
  pageSize = 10;
  public isAnimating = false;
  selectedRows: Set<any> = new Set();
  selectAllChecked = false;
  today = new Date();
  user = {
    name: '',
  };
  studentColumns: string[] = [
    'Event name',
    'Date & time',
    'location',
    'status',
  ];

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private httpService: HttpService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private studentPostLoginFlowService: StudentPostLoginFlowService,
    private urlParser: UrlParserService,
  ) {
    this.route.url.subscribe((segments) => {
      this.isEditPage = segments.some((segment) => segment.path === 'edit');
    });

    const UserDetailsStr = localStorage.getItem('userDetails') || '';
    let name = '';
    try {
      const UserDetails = UserDetailsStr ? JSON.parse(UserDetailsStr) : null;
      if (UserDetails) {
        name =
          (UserDetails.firstName || '') + ' ' + (UserDetails.lastName || '');
      }
    } catch (e) {
      name = '';
    }
    this.user.name = name.trim();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'] as 'admin' | 'teacher' | 'student';
      if (tab && ['admin', 'teacher', 'student'].includes(tab)) {
        this.selectedSegment = tab;
      } else {
        this.selectedSegment = 'admin';
      }
      this.fetchEventsForSegment(this.selectedSegment);
      this.fetchUpNextEvents();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.studentPostLoginFlowService.runIfNeeded();
    }, 200);
  }

  navigatePage(type: string) {
    const routes: { [key: string]: string } = {
      Profile: '/dashboard/user/edit',
      Settings: '/settings',
    };

    const route = routes[type];
    if (route) {
      this.router.navigate([route]);
    }
  }

  goBack(): void {
    window.history.back();
  }

  fetchEventsForSegment(segment: 'admin' | 'teacher' | 'student'): void {
    let source: string;

    switch (segment) {
      case 'admin':
        source = 'admin';
        break;
      case 'teacher':
        source = 'teacher';
        break;
      case 'student':
        source = 'student';
        break;
      default:
        source = 'admin';
    }

    this.fetchUpcomingEvents('all');
  }

  fetchUpcomingEvents(source: string): void {
    this.loading = true;
    this.httpService
      .get<APIResponse>(`studentdashboard?source=${source}`)
      .subscribe({
        next: (response) => {
          const raw = response.data.items || [];
          this.rows = raw
            .filter((item: any) => !this.shouldHideStudentDashboardEvent(item))
            .map((item: any) => {
              const startDate = new Date(item?.eventStartDate);
              const startEndDate = new Date(item?.eventEndDate);

              const formattedStartDate = startDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });
              const formattedEndDate = startEndDate.toLocaleDateString(
                'en-GB',
                {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                },
              );
              const formattedDate =
                formattedEndDate && formattedStartDate !== formattedEndDate
                  ? `${formattedStartDate} - ${formattedEndDate}`
                  : formattedStartDate;

              const status = this.getStudentDashboardStatus(item);

              const statusClass =
                status === 'Interested'
                  ? 'status-interested'
                  : status === 'Declined'
                    ? 'status-inactive'
                    : status === 'Awaiting Approval'
                      ? 'awaiting-status'
                      : status === 'Unapproved'
                        ? 'status-unapproved'
                        : '';

              return {
                id: item?.eventId ?? item?.id,
                'Event name': item?.eventName ?? item?.name ?? '-',
                source: (() => {
                  const cb = (item?.createdBy ?? '').toLowerCase().replace(/\s+/g, '');
                  if (cb === 'superadmin' || cb === 'csqadmin' || cb === 'csquser') return 'CSQ';
                  if (cb === 'teacher') return 'Teacher';
                  if (cb === 'student') return 'Student';
                  if (!cb) return '-';
                  return item.createdBy.charAt(0).toUpperCase() + item.createdBy.slice(1);
                })(),
                'Teacher name': item?.teacherName ?? '-',
                'Date & time': {
                  date: formattedDate,
                  time: item?.eventTime,
                },
                // SORT VALUE
                _dateTimeSort: startDate.getTime(),
                status: status ?? '-',
                statusClass,
                location: item?.location ?? '-',
                approval: item?.eventAdminApprovalStatus ?? '-',
                _raw: item,
              };
            });
        },
        error: (err) => {
          this.loading = false;
          console.log(err);
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        },
        complete: () => (this.loading = false),
      });
  }

  isFutureDate(dateStr: string): boolean {
    const date = new Date(dateStr);
    const today = new Date();

    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return date > today;
  }

  fetchUpNextEvents(): void {
    this.loading = true;

    this.httpService
      .get<APIResponse>('studentdashboard?source=admin')
      .subscribe({
        next: (response) => {
          const events = response.data.items || [];
          const allowedEventStatuses = ['upcoming', 'on today'];
          const futureEvent = events
            .filter(
              (item: any) =>
                // CSQ (Internal) events only — exclude teacher-created (External) and student work experience
                item?.eventScope?.toLowerCase() === 'internal' &&
                !this.shouldHideStudentDashboardEvent(item) &&
                // Only show Upcoming and On Today (Ongoing) events
                allowedEventStatuses.includes(
                  (item?.eventStatus ?? '').toString().trim().toLowerCase(),
                ),
            )
            .sort((a: any, b: any) => {
              // On Today first, then Upcoming
              const aStatus = (a?.eventStatus ?? '').toString().trim().toLowerCase();
              const bStatus = (b?.eventStatus ?? '').toString().trim().toLowerCase();
              if (aStatus === bStatus) return 0;
              return aStatus === 'on today' ? -1 : 1;
            });
          this.nextEvents = futureEvent.length
            ? futureEvent.slice(0, 3).map((item: any) => {
                const status = this.getStudentDashboardStatus(item);

                const statusClass =
                  status === 'Interested'
                    ? 'status-interested'
                    : status === 'Declined'
                      ? 'status-inactive'
                      : status === 'Awaiting Approval'
                        ? 'awaiting-status'
                        : status === 'Unapproved'
                          ? 'status-unapproved'
                          : status === 'Approved'
                            ? 'status-active'
                            : '';

                return {
                  ...item,
                  status: status ?? '-',
                  statusClass,
                };
              })
            : [];
        },
        error: (err) => {
          this.loading = false;
          this.showErrorToast(
            err.message || 'Something went wrong. Please try again later.',
          );
        },
        complete: () => (this.loading = false),
      });
  }

  private getStudentDashboardStatus(item: any): string {
    if (item?.registrationStatus === 'TeacherReviewPending') {
      return item?.createdBy?.toLowerCase() === 'student' ? 'Awaiting Approval' : 'Interested';
    }

    if (item?.registrationStatus === 'Interested') {
      return 'Interested';
    }

    if (item?.registrationStatus === 'TeacherApproved') {
      return 'Approved';
    }

    if (
      item?.registrationStatus === 'TeacherRejected' ||
      item?.registrationStatus === 'AdminRejected'
    ) {
      return item?.createdBy?.toLowerCase() === 'student' ? 'Declined' : 'Unapproved';
    }

    return item?.status ?? '-';
  }

  private shouldHideStudentDashboardEvent(item: any): boolean {
    const status = this.getStudentDashboardStatus(item).toString().trim().toLowerCase();
    const registrationStatus = (item?.registrationStatus ?? '')
      .toString()
      .trim()
      .toLowerCase();
    const rawStatus = (item?.status ?? '').toString().trim().toLowerCase();
    const adminApprovalStatus = (item?.eventAdminApprovalStatus ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return (
      status === 'unapproved' ||
      status === 'declined' ||
      status === 'rejected' ||
      // Raw status values that the table maps to "Unapproved" badge
      rawStatus === 'teacherrejected' ||
      rawStatus === 'adminrejected' ||
      rawStatus === 'unapproved' ||
      rawStatus === 'declined' ||
      rawStatus === 'rejected' ||
      // Registration status checks
      registrationStatus === 'teacherrejected' ||
      registrationStatus === 'adminrejected' ||
      registrationStatus === 'rejected' ||
      registrationStatus === 'declined' ||
      // Event-level admin approval — if the event itself is unapproved, hide it
      adminApprovalStatus === 'unapproved'
    );
  }

  getEventImage(event: any): string {
    const fallback = 'assets/images/placeholder-image.svg';
    const pdfIcon = '../../../../assets/images/pdf-icon.png';

    let filePath: string | null = null;
    // Only use eventTypeImage (event banner) — never eventImages (student evidence uploads)
    const raw = event?.eventTypeImage || null;

    if (!raw) return fallback;
    if (Array.isArray(raw)) {
      filePath = raw[0];
    } else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          filePath = parsed[0];
        } else {
          filePath = raw;
        }
      } catch {
        filePath = raw;
      }
    }

    if (!filePath) return fallback;
    const cleanPath = filePath.split('?')[0].toLowerCase();
    if (cleanPath.endsWith('.pdf')) {
      return pdfIcon;
    }

    return filePath;
  }

  onRegister(eventId: any): void {
    if (eventId) {
      this.httpService
        .post<APIResponse>(`studenteventregistrations/register/${eventId}`, {})
        .subscribe({
          next: (response) => {
            if (response?.status) {
              this.fetchEventsForSegment(this.selectedSegment);
              this.showSuccessToast(response.message);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (error) => {
            this.showErrorToast(error?.message);
          },
        });
    } else {
      this.showErrorToast('Event ID not found');
    }
  }

  onStatusClick(event: { row: any; id: any }): void {
    const { row, id } = event;

    if (row.status == 'Unregistered') {
      this.onRegister(id);
      return;
    }

    if (
      row.status !== 'Approved' ||
      row.status !== 'Submitted (Pending Teacher Approval)'
    ) {
      const dialogData: AlertDialogData = {
        title: 'Unregister from event?',
        message: 'Are you sure you want to continue?',
        okText: 'Unregister',
        cancelText: 'Cancel',
        icon: 'warning',
        submessage: '',
        cancelButtonColor: '#007BFF',
        okButtonColor: '#EF4A00 ',
      };

      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '25.438rem',
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (id) {
            this.httpService
              .post<APIResponse>(
                `StudentEventRegistrations/unregister/${id}`,
                {},
              )
              .subscribe({
                next: (response) => {
                  if (response?.status) {
                    this.fetchEventsForSegment(this.selectedSegment);
                    this.showSuccessToast(response.message);
                  } else {
                    this.showErrorToast(response.message);
                  }
                },
                error: (err) => {
                  this.showErrorToast(err?.message);
                },
              });
          } else {
            this.showErrorToast('Event ID not found');
          }
        }
      });
    }
  }

  onRowSelected(selected: any[]) {
    console.log(selected);
  }

  onRowClicked(row: any) {
    console.log('clicked', row);
  }
  edit(row: any) {
    console.log('clicked', row);
  }
  remove(row: any) {
    console.log('clicked', row);
  }

  getSortedData(): any[] {
    if (!this.sortColumn || !this.sortDirection) {
      return this.rows.slice();
    }
    const sorted = this.rows.slice().sort((a, b) => {
      let aVal = a[this.sortColumn!];
      let bVal = b[this.sortColumn!];
      const aNum = typeof aVal === 'number' ? aVal : parseFloat(aVal);
      const bNum = typeof bVal === 'number' ? bVal : parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return this.sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      aVal = (aVal || '').toString().toLowerCase();
      bVal = (bVal || '').toString().toLowerCase();
      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }

  get paginatedData(): any[] {
    let sorted = this.getSortedData();
    const start = (this.currentPage - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  goToPage(page: number) {
    if (page === this.currentPage || this.isAnimating) return;

    this.isAnimating = true;
    setTimeout(() => {
      this.currentPage = page;
      this.updateSelectAllState();
      this.isAnimating = false;
    }, 300);
  }

  private updateSelectAllState() {
    const validRowsOnCurrentPage = this.paginatedData.filter((row) =>
      this.isRowValid(row),
    );
    const selectedValidRowsOnCurrentPage = validRowsOnCurrentPage.filter(
      (row) => this.selectedRows.has(row),
    );
    this.selectAllChecked =
      validRowsOnCurrentPage.length > 0 &&
      selectedValidRowsOnCurrentPage.length === validRowsOnCurrentPage.length;
  }

  isRowValid(row: any): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d+$/;

    if (
      this.columns.includes('email') &&
      this.columns.includes('phoneNumber')
    ) {
      return (
        !!row.firstName &&
        !!row.lastName &&
        emailRegex.test(row.email || '') &&
        phoneRegex.test(row.phoneNumber || '')
      );
    }

    if (
      this.columns.includes('email (Personal)') &&
      this.columns.includes('mobileNumber')
    ) {
      return (
        !!row.firstName &&
        !!row.lastName &&
        emailRegex.test(row['email (Personal)'] || '') &&
        phoneRegex.test(row.mobileNumber || '')
      );
    }

    return this.columns.every(
      (col) => row[col] && row[col].toString().trim() !== '',
    );
  }

  openFaqDialog(width: string = '50.5rem'): void {
    const role = localStorage.getItem('userRole');
    let audienceType: 0 | 1 | 2 = 0;
    if (role === 'teacher') {
      audienceType = 1;
    } else if (role === 'csqadmin' || role === 'csquser') {
      audienceType = 2;
    }

    this.dialog.open(AccordionComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { audienceType },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });
  }

  onEdit(row: any): void {
    this.openExternalEventDialog(row, true);
  }

  openExternalEventDialog(
    eventData: any = null,
    isEdit: boolean = false,
    width: string = '43.125rem',
  ): void {
    const dialogData = {
      initialData: eventData?._raw || eventData,
      EditMode: isEdit,
      ViewMode: false,
    };

    const dialogRef = this.dialog.open(SubmitEventComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: dialogData,
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.fetchEventsForSegment(this.selectedSegment);
      }
    });
  }

  onRegisterClick(event: any): void {
    const dialogData: AlertDialogData = {
      title: 'Status update',
      message: `Would you like to register?`,
      okText: 'Yes',
      cancelText: 'No',
      icon: 'warning',
      submessage: '',
      dialogBgColor: '#404040',
      okButtonColor: '#14B8A6',
      cancelButtonColor: '#171717',
    };
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        let id = event?.id;
        this.httpService
          .post<APIResponse>(`studenteventregistrations/register/${id}`, {})
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: () => {
              this.showErrorToast(
                'An error occurred while updating the audience status.',
              );
            },
          });
      }
    });
  }

  switchSegment(segment: 'admin' | 'teacher' | 'student') {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: segment },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onView(event: any): void {
    if (!event) {
      console.warn('onView called without event');
      return;
    }

    const eventId = event?.id ?? event?.eventId;

    if (!eventId) {
      console.error('Missing event ID', { event });
      return;
    }

    const source = event?.source ?? event?.createdBy;
    const sourceLower = typeof source === 'string' ? source.toLowerCase() : 'admin';

    // Normalize any admin-type source (e.g. "super admin", "csqadmin") to 'admin'
    const eventCreator =
      sourceLower === 'teacher' || sourceLower === 'student'
        ? sourceLower
        : 'admin';

    const encodedId = this.urlParser.encodeId(eventId.toString());
    const encodedSegment = this.urlParser.encodeId(eventCreator); 

    this.router.navigate(['/events/event-list/view', encodedId], {
      queryParams: {
        segment: encodedSegment,
      },
    });
  }

  onAttenedClick(event: any): void {
    const dialogData: AlertDialogData = {
      title: 'Status update',
      message: `Would you like to Attend?`,
      okText: 'Yes',
      cancelText: 'No',
      icon: 'warning',
      submessage: '',
      dialogBgColor: '#404040',
      okButtonColor: '#14B8A6',
      cancelButtonColor: '#171717',
    };
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        let id = '378372idhn3';
        this.httpService
          .post<APIResponse>(`teacherevent/${id}/attendance`, {
            isAttended: true,
          })
          .subscribe({
            next: (response) => {
              if (response?.status) {
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: () => {
              this.showErrorToast(
                'An error occurred while updating the audience status.',
              );
            },
          });
      }
    });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
